import { get } from 'lodash';

import type { MaasGroupSpec } from '../../../../capabilities/maas-init/maasTypes';
import { DEFAULT_QUANTITY_UNITS, normalizeQuantityUnit } from '../../../../components/quantityUnit';
import type { ResourceType } from '../../../../platform/pipeline-kernel/types';
import { resolveGroupReplicas, shouldIncludeMasterGroup } from './nodeGroups';
import type { DisplayRow, MaasComputeTemplates, MaasNodeGroupsSnapshot, ToResourceConfigFn } from './types';

function resolveMaasResourceQuantity(resource: Record<string, unknown> | undefined, quantityKeys: string[]) {
  if (!resource) return undefined;
  for (const key of quantityKeys) {
    const qty = get(resource, key);
    if (qty !== undefined && qty !== null && qty !== '') return qty;
  }
  return undefined;
}

function multiplyRequirement(value: unknown, replicas: number): number | undefined {
  if (value == null || value === '') return undefined;
  const asNumber = Number(value);
  if (Number.isFinite(asNumber) && asNumber > 0) return asNumber * replicas;
  // 兼容 Maas CRD「8Gi」字符串
  const stripped = Number(String(value).trim().replace(/Gi$/i, '').trim());
  if (Number.isFinite(stripped) && stripped > 0) return stripped * replicas;
  return undefined;
}

function mergeMaasComputePricingPart(
  resource: Record<string, unknown>,
  kind: 'cpu' | 'memory' | 'gpu',
): Record<string, unknown> | undefined {
  const part = resource[kind] as Record<string, unknown> | undefined;
  const pricingKey = kind === 'cpu' ? 'cpuPricing' : kind === 'memory' ? 'memoryPricing' : 'gpuPricing';
  const pricing = resource[pricingKey] as Record<string, unknown> | undefined;
  if (!part && !pricing) return undefined;
  return {
    ...part,
    ...(pricing?.vendor != null && pricing.vendor !== '' ? { vendor: pricing.vendor } : {}),
    ...(pricing?.device != null && pricing.device !== '' ? { device: pricing.device } : {}),
    ...(pricing?.deviceType != null && pricing.deviceType !== '' ? { deviceType: pricing.deviceType } : {}),
    ...(pricing?.vendorID != null && pricing.vendorID !== '' ? { vendorID: pricing.vendorID } : {}),
    ...(pricing?.deviceID != null && pricing.deviceID !== '' ? { deviceID: pricing.deviceID } : {}),
    ...(pricing?.quantityUnit != null && pricing.quantityUnit !== '' ? { quantityUnit: pricing.quantityUnit } : {}),
  };
}

function appendSingleComputeConfig(
  result: ResourceType[],
  template: ResourceType,
  resourcePart: Record<string, unknown> | undefined,
  quantityKeys: string[],
  replicas: number,
  toResourceConfig: ToResourceConfigFn,
) {
  const request = resolveMaasResourceQuantity(resourcePart, quantityKeys);
  const requirement = multiplyRequirement(request, replicas);
  const device = resourcePart?.device != null && resourcePart.device !== '' ? String(resourcePart.device) : undefined;
  const config = toResourceConfig(template, {
    vendor: resourcePart?.vendor as string | undefined,
    device,
    deviceType: resourcePart?.deviceType as string | undefined,
    vendorID: resourcePart?.vendorID as string | undefined,
    deviceID: resourcePart?.deviceID as string | undefined,
    requirement,
    quantityUnit: normalizeQuantityUnit(resourcePart?.quantityUnit),
  });
  if (config) result.push(config);
}

function resolveInstanceReplicas(instanceReplicas?: number): number {
  return typeof instanceReplicas === 'number' && Number.isFinite(instanceReplicas) && instanceReplicas > 0 ? instanceReplicas : 1;
}

function appendGroupComputeResourceConfigs(
  result: ResourceType[],
  group: MaasGroupSpec | undefined,
  templates: MaasComputeTemplates,
  toResourceConfig: ToResourceConfigFn,
  instanceReplicas = 1,
) {
  if (!group) return;
  /** 节点组副本 × 拓扑/外部容器副本 */
  const replicas = resolveGroupReplicas(group) * resolveInstanceReplicas(instanceReplicas);
  const resource = group.resource as Record<string, unknown> | undefined;
  if (!resource) return;

  appendSingleComputeConfig(
    result,
    templates.vcpu,
    mergeMaasComputePricingPart(resource, 'cpu'),
    ['request', 'cores'],
    replicas,
    toResourceConfig,
  );
  appendSingleComputeConfig(
    result,
    templates.memory,
    mergeMaasComputePricingPart(resource, 'memory'),
    ['request', 'resource.memory.request'],
    replicas,
    toResourceConfig,
  );
  appendSingleComputeConfig(
    result,
    templates.gpu,
    mergeMaasComputePricingPart(resource, 'gpu'),
    ['number', 'card.request', 'resource.nvidia.com/gpu.request'],
    replicas,
    toResourceConfig,
  );
}

function forEachMaasNodeGroup(
  nodes: MaasNodeGroupsSnapshot,
  callback: (group: MaasGroupSpec, role: 'master' | 'worker', index: number) => void,
) {
  if (shouldIncludeMasterGroup(nodes.runMode) && nodes.masterNodes[0]) {
    callback(nodes.masterNodes[0], 'master', 0);
  }
  nodes.workerNodes.forEach((worker, index) => {
    callback(worker, 'worker', index);
  });
}

function resolveNumericQuantity(value: unknown): number {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : 0;
}

export function appendAggregatedMaasNodeComputeDisplayRows(
  pushRow: (item: DisplayRow) => void,
  nodes: MaasNodeGroupsSnapshot,
  templates: MaasComputeTemplates,
  labels: { cpu: string; memory: string; gpu: string },
  options?: { instanceReplicas?: number },
) {
  let cpuTotal = 0;
  let memoryTotal = 0;
  let cpuUnit: string | undefined;
  let memoryUnit: string | undefined;
  const gpuMap = new Map<string, { device: string; vendor?: string; count: number; unit?: string }>();
  const instanceReplicas = resolveInstanceReplicas(options?.instanceReplicas);

  forEachMaasNodeGroup(nodes, (group) => {
    const replicas = resolveGroupReplicas(group) * instanceReplicas;
    const resource = group.resource as Record<string, unknown> | undefined;
    if (!resource) return;

    const cpuPart = mergeMaasComputePricingPart(resource, 'cpu');
    const memoryPart = mergeMaasComputePricingPart(resource, 'memory');
    const cpuQty = resolveMaasResourceQuantity(cpuPart, ['request', 'cores']);
    const memoryQty = resolveMaasResourceQuantity(memoryPart, ['request', 'resource.memory.request']);
    cpuTotal += resolveNumericQuantity(cpuQty) * replicas;
    memoryTotal += resolveNumericQuantity(memoryQty) * replicas;
    if (!cpuUnit) cpuUnit = normalizeQuantityUnit(cpuPart?.quantityUnit);
    // 内存展示固定 Gi（表单数量为 Gi）
    if (!memoryUnit) memoryUnit = DEFAULT_QUANTITY_UNITS.memory;

    const gpuPart = mergeMaasComputePricingPart(resource, 'gpu');
    const gpuQty = resolveMaasResourceQuantity(gpuPart, ['number', 'card.request', 'resource.nvidia.com/gpu.request']);
    const gpuCount = resolveNumericQuantity(gpuQty) * replicas;
    if (gpuCount <= 0 || !gpuPart) return;

    const device = String(gpuPart.device ?? gpuPart.deviceType ?? 'GPU').trim() || 'GPU';
    const vendor = gpuPart.vendor != null && gpuPart.vendor !== '' ? String(gpuPart.vendor) : undefined;
    const unit = normalizeQuantityUnit(gpuPart?.quantityUnit);
    const existing = gpuMap.get(device);
    if (existing) {
      existing.count += gpuCount;
      if (!existing.vendor && vendor) existing.vendor = vendor;
      if (!existing.unit && unit) existing.unit = unit;
    } else {
      gpuMap.set(device, { device, vendor, count: gpuCount, unit });
    }
  });

  if (cpuTotal > 0) {
    const unit = cpuUnit || templates.vcpu.unit;
    pushRow({
      id: 'compute-cpu-total',
      label: `${labels.cpu} (总)`,
      value: unit ? `${cpuTotal} ${unit}` : String(cpuTotal),
    });
  }

  if (memoryTotal > 0) {
    const unit = memoryUnit || DEFAULT_QUANTITY_UNITS.memory;
    pushRow({
      id: 'compute-memory-total',
      label: `${labels.memory} (总)`,
      value: unit ? `${memoryTotal} ${unit}` : String(memoryTotal),
    });
  }

  if (gpuMap.size > 0) {
    const gpuEntries = Array.from(gpuMap.values());
    const value = gpuEntries
      .map((item) => {
        const unit = item.unit || templates.gpu.unit;
        const qtyText = unit ? `${item.count} ${unit}` : String(item.count);
        return `${item.device} * ${qtyText}`;
      })
      .join('、');
    const vendors = Array.from(new Set(gpuEntries.map((item) => item.vendor).filter(Boolean))) as string[];
    pushRow({
      id: 'compute-gpu-total',
      label: labels.gpu,
      value,
      ...(vendors.length ? { tag: vendors.join('、') } : {}),
    });
  }
}

export function appendMaasNodeComputeResourceConfigs(
  result: ResourceType[],
  nodes: MaasNodeGroupsSnapshot,
  templates: MaasComputeTemplates,
  toResourceConfig: ToResourceConfigFn,
  options?: { instanceReplicas?: number },
) {
  const instanceReplicas = resolveInstanceReplicas(options?.instanceReplicas);
  if (shouldIncludeMasterGroup(nodes.runMode)) {
    appendGroupComputeResourceConfigs(result, nodes.masterNodes[0], templates, toResourceConfig, instanceReplicas);
  }
  nodes.workerNodes.forEach((worker) => {
    appendGroupComputeResourceConfigs(result, worker, templates, toResourceConfig, instanceReplicas);
  });
}
